import { useNavigate } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client.js";
import { useAppSession } from "./AppSession.js";
import styles from "./WorkspaceGate.module.css";

export function WorkspaceGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useAppSession();
  const titleId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const [name, setName] = useState("");
  const [actionError, setActionError] = useState<unknown>(null);
  const [exitPending, setExitPending] = useState(false);
  const identity = session.identity;

  if (identity === null) return null;

  const simulation = identity.simulation !== null;
  const visibleError = actionError ?? session.workspaceCreateError;
  const errorMessage = visibleError instanceof ApiError
    ? t(visibleError.messageKey, { ...visibleError.params, defaultValue: t("errors.generic") })
    : visibleError === null
      ? null
      : t("errors.generic");

  const createWorkspace = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActionError(null);
    void session.createWorkspace(name)
      .then(async (workspace) => {
        await navigate({
          to: "/focus",
          search: { workspace: workspace.id, mode: "new" },
          replace: true,
        });
      })
      .catch((error: unknown) => setActionError(error));
  };

  const exitSimulation = () => {
    setActionError(null);
    setExitPending(true);
    void session.endSimulation()
      .then(async () => {
        await navigate({ to: "/focus", search: {}, replace: true });
      })
      .catch((error: unknown) => setActionError(error))
      .finally(() => setExitPending(false));
  };

  return (
    <main className={styles.page} aria-labelledby={titleId}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>{t("app.name")}</p>
        <h1 id={titleId}>{simulation ? t("workspaceGate.simulationTitle") : t("workspaceGate.title")}</h1>
        <p className={styles.description} id={descriptionId}>
          {simulation ? t("workspaceGate.simulationDescription") : t("workspaceGate.description")}
        </p>
        {simulation ? (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={exitSimulation}
            disabled={exitPending}
          >
            {exitPending ? t("workspaceGate.exitingSimulation") : t("workspaceGate.exitSimulation")}
          </button>
        ) : (
          <form className={styles.form} onSubmit={createWorkspace}>
            <label htmlFor={nameId}>{t("workspaceGate.nameLabel")}</label>
            <input
              id={nameId}
              name="workspaceName"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("workspaceGate.namePlaceholder")}
              aria-describedby={descriptionId}
              autoComplete="organization"
              maxLength={200}
              required
              autoFocus
              disabled={session.workspaceCreatePending}
            />
            <button type="submit" className={styles.primaryButton} disabled={session.workspaceCreatePending}>
              {session.workspaceCreatePending ? t("workspaceGate.creating") : t("workspaceGate.create")}
            </button>
          </form>
        )}
        {errorMessage !== null && <p className={styles.error} role="alert">{errorMessage}</p>}
      </section>
    </main>
  );
}
